import React, { useEffect, useState, useRef, useCallback } from "react";
import Layout from "../Layout/Layout";
import styled from "styled-components";
import {
  getNotificationForUser,
  getAnyProfileDetails,
} from "./utilities/ApiCalls";
import { getTokenDetails } from "./utilities/CommonFunction";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { setPageTitle } from "../utils/titleUtils";
import { useLocation } from "react-router-dom/cjs/react-router-dom";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [t] = useTranslation();
  const observerRef = useRef(null);
  const loaderRef = useRef(null);

  const location = useLocation();

  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);

  const fetchUserDetails = async (userId) => {
    try {
      const response = await getAnyProfileDetails(userId);
      return {
        name: response.data.data.name,
        profilePic: response.data.data.profilePic,
      };
    } catch (error) {
      console.error("Error fetching user details:", error);
      return { name: "Unknown User", profilePic: "defaultProfilePicUrl" };
    }
  };

  const fetchNotifications = async (page = 1, append = false) => {
    try {
      let token = localStorage.getItem("loginDetails");
      let resp = {};

      if (token) {
        resp = getTokenDetails(token);
      }
      const userId = resp._id;

      const response = await getNotificationForUser({ 
        userId, 
        pageNo: page 
      });
      
      const notificationsData = response.data.notifications || [];
      console.log(response);
      const pagination = response.pagination || {};
      
      // Update pagination state
      setHasNextPage(pagination.hasNextPage || false);
      setCurrentPage(page);

      const notificationsWithUserDetails = await Promise.all(
        notificationsData.map(async (notif) => {
          const { name, profilePic } = await fetchUserDetails(notif.userId);
          return { ...notif, name, profilePic };
        })
      );

      // Either append new notifications or replace existing ones
      if (append) {
        setNotifications(prev => [...prev, ...notificationsWithUserDetails]);
      } else {
        setNotifications(notificationsWithUserDetails);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setHasNextPage(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Initialize notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Setup intersection observer for infinite scroll
  const lastNotificationRef = useCallback(node => {
    if (loading || loadingMore) return;
    
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage) {
        loadMoreNotifications();
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loading, loadingMore, hasNextPage]);

  const loadMoreNotifications = () => {
    if (!hasNextPage || loadingMore) return;
    
    setLoadingMore(true);
    fetchNotifications(currentPage + 1, true);
  };

  return (
    <Layout>
      <Container>
        {loading ? (
          <LoaderContainer>
            <ThreeDots color="#00BFFF" height={80} width={80} />
          </LoaderContainer>
        ) : (
          <NotificationContainer>
            <Header>{t("Notifications.Notifications")}</Header>
            {notifications.length === 0 ? (
              <NoNotification>{t("No Notifications")}</NoNotification>
            ) : (
              <>
                {notifications.map((notif, index) => (
                  <NotificationCard 
                    key={notif.id || index} 
                    unread={notif.readStatus !== 1}
                    ref={index === notifications.length - 1 ? lastNotificationRef : null}
                  >
                    <ProfilePic
                      alt={notif.name}
                      src={notif.profilePic || "../Images/defaultProfilePic.png"}
                    />
                    <Info>
                      <UserName>
                        {notif.name}{" "}
                        {notif.type === "like"
                          ? "liked your post"
                          : notif.type === "comment"
                          ? "commented on your post"
                          : "started following you"}
                      </UserName>
                      {notif.createdAt &&
                        !isNaN(new Date(notif.createdAt).getTime()) && (
                          <Timestamp>
                            {new Date(notif.createdAt).toLocaleString()}
                          </Timestamp>
                        )}
                    </Info>
                  </NotificationCard>
                ))}
                
                {loadingMore && (
                  <LoadingMore ref={loaderRef}>
                    <ThreeDots color="#00BFFF" height={40} width={40} />
                  </LoadingMore>
                )}
                
                {!loadingMore && hasNextPage && (
                  <LoadMoreButton onClick={loadMoreNotifications}>
                    Load More
                  </LoadMoreButton>
                )}
                
                {!hasNextPage && notifications.length > 0 && (
                  <EndMessage>No more notifications</EndMessage>
                )}
              </>
            )}
          </NotificationContainer>
        )}
      </Container>
    </Layout>
  );
};

export default NotificationPage;

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: #0f1817;
  min-height: 100vh;
  padding: 20px;
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
`;

const NotificationContainer = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background-color: #1b2838;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin-top: 20px;
`;

const Header = styled.h2`
  color: #fff;
  margin-bottom: 20px;
`;

const NotificationCard = styled.div`
  width: 100%;
  background-color: ${({ unread }) =>
    unread
      ? "#324455"
      : "#2a3948"}; // Lighter background for unread notifications
  border-radius: 10px;
  display: flex;
  align-items: center;
  padding: 20px;
  margin: 10px 0;
  transition: transform 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ProfilePic = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 20px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const UserName = styled.p`
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  margin: 0;
`;

const Timestamp = styled.span`
  color: #9ca3af;
  font-size: 12px;
  margin-top: 5px;
`;

const NoNotification = styled.p`
  color: white;
  font-size: 18px;
  text-align: center;
  width: 100%;
  padding: 20px;
`;

const LoadMoreButton = styled.button`
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  margin-top: 20px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #3182ce;
  }
`;

const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px 0;
`;

const EndMessage = styled.p`
  color: #9ca3af;
  margin-top: 20px;
  text-align: center;
`;